// ModCreatePlats — generiskt platsformulär (build 87, P1)
// Mod (master) kan skapa nya kategoripunkter direkt utan kod-deploy:
// Hjärtstartare, Återvinning, Badplats, Vindskydd, Grillplats, Båtramp,
// Offentlig toalett, Rastplats, Lekplats, Utsiktsplats — och alla befintliga
// karttipps-kategorier. Sparas i karttipps-collection (ingen ny DB-struktur)
// och syns publikt på /karta och Home-embedded karta så snart status=Publicerat.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { MapPin, Save, ArrowLeft, ExternalLink } from "lucide-react";
import BackLink from "@/components/BackLink";
import PendingDraftBanner from "@/components/PendingDraftBanner";
import GalleriEditor from "@/components/GalleriEditor";
import TagInput from "@/components/TagInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const modHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` });

// Default-center: Backe by (samma som FlyttaMarkorDialog)
const DEFAULT_CENTER = { lat: 63.81667, lng: 16.4 };

// Nya servicepunkts-kategorier (P1) + bevarade karttipps-kategorier för kompatibilitet.
// Etappe 4.1 taxonomi-fix — nya kategorier inkluderade: Bastu, Busshållplats, Elljusspår,
// Fiskekort, Handla dygnet runt, Idrottsplan, Motionsspår, Ställplats, Turistinformation.
// Grupperad för bättre översikt i select (gruppernas innehåll sorteras alfabetiskt sv-SE).
const KATEGORI_GRUPPER = [
  {
    label: "Orter & socken (geografische Orte)",
    items: ["By", "Historisk plats", "Ort", "Socken"],
  },
  {
    label: "Säkerhet & service",
    items: ["Handla dygnet runt", "Hjärtstartare", "Offentlig toalett", "Turistinformation", "Återvinning"],
  },
  {
    label: "Friluftsliv & natur",
    items: ["Badplats", "Bastu", "Båtramp", "Elljusspår", "Fiskekort", "Grillplats", "Motionsspår", "Rastplats", "Utsiktsplats", "Vindskydd"],
  },
  {
    label: "Resa & boende",
    items: ["Busshållplats", "Ställplats"],
  },
  {
    label: "Barn & idrott",
    items: ["Idrottsplan", "Lekplats"],
  },
  {
    label: "Karttips / sevärdheter (befintligt)",
    items: ["Annat", "Bärställe", "Favoritplats", "Fiskevatten", "Kulturminne", "Naturtips", "Sevärdhet", "Svampställe"],
  },
];

function parseMapInput(raw) {
  if (!raw) return null;
  const str = raw.trim();
  const llMatch = str.match(/^(-?\d{1,3}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)$/);
  if (llMatch) return { lat: parseFloat(llMatch[1]), lng: parseFloat(llMatch[2]) };
  const at = str.match(/@(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/);
  if (at) return { lat: parseFloat(at[1]), lng: parseFloat(at[2]) };
  const q = str.match(/[?&](?:q|query)=(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/);
  if (q) return { lat: parseFloat(q[1]), lng: parseFloat(q[2]) };
  return null;
}

export default function ModCreatePlats() {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);
  const [pos, setPos] = useState(DEFAULT_CENTER);
  const [coordInput, setCoordInput] = useState("");
  const [form, setForm] = useState({
    namn: "",
    kategori: "Hjärtstartare",
    ort: "",
    adress: "",
    beskrivning: "",
    kortbeskrivning: "",
    kalltext: "",
    kalla_lank: "",
    taggar: "",
    media_url: "",
    media_typ: "",
    media_beskrivning: "",
    // Etappe 3 — Audit
    andrad_av: "",
    andringsnotering: "",
    publish: true,
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [savedId, setSavedId] = useState(null);

  // Auth-vakt: utan token → till login
  useEffect(() => {
    if (!sessionStorage.getItem("mod_token")) {
      navigate("/mod/login");
    }
  }, [navigate]);

  // Leaflet-init (en gång)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const L = (await import("leaflet")).default;
      if (cancelled || !mapRef.current) return;
      mapInstance.current = L.map(mapRef.current, {
        center: [pos.lat, pos.lng],
        zoom: 12,
        scrollWheelZoom: true,
      });
      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; OpenStreetMap-bidragsgivare',
        maxZoom: 19,
      }).addTo(mapInstance.current);

      markerRef.current = L.marker([pos.lat, pos.lng], { draggable: true }).addTo(mapInstance.current);
      markerRef.current.on("dragend", (e) => {
        const { lat: la, lng: ln } = e.target.getLatLng();
        setPos({ lat: la, lng: ln });
      });
      mapInstance.current.on("click", (e) => {
        const { lat: la, lng: ln } = e.latlng;
        markerRef.current.setLatLng([la, ln]);
        setPos({ lat: la, lng: ln });
      });
    })();
    return () => {
      cancelled = true;
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []);

  const set = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: null }));
  };

  const handleCoordPaste = () => {
    const parsed = parseMapInput(coordInput);
    if (!parsed) {
      setErrors((prev) => ({ ...prev, coord: "Kunde inte tolka. Använd lat,lng eller en Google Maps-länk." }));
      return;
    }
    setErrors((prev) => ({ ...prev, coord: null }));
    setPos({ lat: parsed.lat, lng: parsed.lng });
    if (mapInstance.current && markerRef.current) {
      markerRef.current.setLatLng([parsed.lat, parsed.lng]);
      mapInstance.current.setView([parsed.lat, parsed.lng], 14);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.namn.trim()) e.namn = "Ange ett namn";
    if (!form.kategori) e.kategori = "Välj kategori";
    // Etappe 3 — andrad_av Pflicht (svenska felmeddelandet ord-för-ord ur spec)
    if (!form.andrad_av.trim()) e.andrad_av = "Ange namn på den som ändrar posten innan du sparar.";
    if (form.kalla_lank && !/^https?:\/\//.test(form.kalla_lank)) {
      e.kalla_lank = "Länk måste börja med http:// eller https://";
    }
    return e;
  };

  const submit = async (publishOverride) => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        namn: form.namn.trim(),
        kategori: form.kategori,
        ort: form.ort.trim() || null,
        adress: form.adress.trim() || null,
        beskrivning: form.beskrivning.trim() || null,
        kortbeskrivning: form.kortbeskrivning.trim() || null,
        kalltext: form.kalltext.trim() || null,
        kalla_lank: form.kalla_lank.trim() || null,
        taggar: form.taggar.split(",").map(s => s.trim()).filter(Boolean),
        media_url: form.media_url.trim() || null,
        media_typ: form.media_typ || null,
        media_beskrivning: form.media_beskrivning.trim() || null,
        // Etappe 3 — Audit
        andrad_av: form.andrad_av.trim(),
        andringsnotering: form.andringsnotering.trim() || null,
        lat: pos.lat,
        lng: pos.lng,
        publish: typeof publishOverride === "boolean" ? publishOverride : form.publish,
      };
      const res = await axios.post(`${API}/mod/karttipps/create`, payload, { headers: modHeaders() });
      setSavedId(res.data.id);
      setBusy(false);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/mod/login");
        return;
      }
      setErrors({ submit: err.response?.data?.detail || "Kunde inte spara" });
      setBusy(false);
    }
  };

  if (savedId) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="mod-create-plats-success">
        <BackLink to="/mod" label="Tillbaka till moderatorvy" />
        <div className="bg-pine/10 border border-pine/30 rounded-lg p-6 mt-4">
          <h1 className="font-heading text-2xl font-medium text-ink mb-2">Plats skapad ✓</h1>
          <p className="text-base text-bark mb-4">
            <strong>{form.namn}</strong> ({form.kategori}) har sparats som <em>{form.publish ? "Publicerat" : "Utkast"}</em>.
            {form.publish && " Den syns nu på /karta och i startsidans karta."}
          </p>
          {/* Build 96.21 — Post-Create-Bild-Upload direkt efter att posten skapats. */}
          <div className="bg-white rounded border border-warm-border p-3 mb-4" data-testid="post-create-galleri">
            <p className="text-sm font-medium text-ink mb-1">Lägg till bilder (frivilligt)</p>
            <p className="text-xs text-bark/70 mb-2">
              Bilder: JPG, PNG, WebP, GIF, HEIC · max 20 MB · max 3 st.
              Video-uppladdning stöds inte här — använd media-URL-fältet ovan om du behöver en YouTube-länk.
            </p>
            <GalleriEditor
              collection="karttipps"
              itemId={savedId}
              bilder={[]}
              onChange={() => {}}
            />
          </div>
          <div className="flex flex-wrap gap-3 mt-4">
            <button
              data-testid="mod-create-plats-new"
              onClick={() => {
                setSavedId(null);
                setForm({
                  namn: "", kategori: form.kategori, ort: form.ort, adress: "",
                  beskrivning: "", kortbeskrivning: "", kalltext: "", kalla_lank: "",
                  taggar: "", media_url: "", media_typ: "", media_beskrivning: "",
                  andrad_av: form.andrad_av, andringsnotering: "",
                  publish: true,
                });
                setCoordInput("");
              }}
              className="bg-pine hover:bg-pine-hover text-white font-medium px-5 py-2.5 rounded-md text-sm"
            >
              Skapa ytterligare en plats
            </button>
            <a
              href="/karta"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mod-create-plats-visa-karta"
              className="inline-flex items-center gap-1.5 bg-falu hover:bg-falu-hover text-parchment font-medium px-5 py-2.5 rounded-md text-sm no-underline"
            >
              Öppna /karta i ny flik <ExternalLink size={14} />
            </a>
            <button onClick={() => navigate("/mod")} className="text-bark hover:text-ink px-3 py-2.5 text-sm">
              Klar — tillbaka
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="mod-create-plats-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <BackLink to="/mod" label="Moderatorvy" />
      <PendingDraftBanner urlPattern="/api/mod/karttipps" />
      <div className="flex items-center gap-2 mb-2">
        <MapPin size={22} className="text-falu" />
        <h1 className="font-heading text-2xl font-medium text-ink">Skapa ny plats</h1>
      </div>
      <p className="text-base text-muted-foreground mb-6">
        Lägg till en ny servicepunkt eller karttips direkt — t.ex. en hjärtstartare, en badplats eller ett vindskydd.
        Platsen visas omedelbart på <em>/karta</em> och på startsidan när du publicerar.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6">
        {/* Vänster: formulärfält */}
        <div className="space-y-4">
          <Field label="Namn" error={errors.namn} required>
            <input
              data-testid="plats-namn"
              value={form.namn}
              onChange={(e) => set("namn", e.target.value)}
              placeholder="T.ex. Hjärtstartare ICA Backe"
              className="input-mod"
            />
          </Field>

          <Field label="Kategori" error={errors.kategori} required>
            <select
              data-testid="plats-kategori"
              value={form.kategori}
              onChange={(e) => set("kategori", e.target.value)}
              className="input-mod"
            >
              {KATEGORI_GRUPPER.map((grp) => (
                <optgroup key={grp.label} label={grp.label}>
                  {grp.items.map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Ort">
              <input
                data-testid="plats-ort"
                value={form.ort}
                onChange={(e) => set("ort", e.target.value)}
                placeholder="Backe, Rossön, Bodum…"
                className="input-mod"
              />
            </Field>
            <Field label="Adress">
              <input
                data-testid="plats-adress"
                value={form.adress}
                onChange={(e) => set("adress", e.target.value)}
                placeholder="Gata + nr (valfritt)"
                className="input-mod"
              />
            </Field>
          </div>

          <Field label="Kort exposé" hint="Max 300 tecken. Visas i listor.">
            <textarea
              data-testid="plats-kortbeskrivning"
              value={form.kortbeskrivning}
              maxLength={300}
              onChange={(e) => set("kortbeskrivning", e.target.value)}
              rows={2}
              className="input-mod"
              placeholder="T.ex. Hjärtstartare i farstun, tillgänglig dygnet runt."
            />
          </Field>

          <Field label="Beskrivning">
            <textarea
              data-testid="plats-beskrivning"
              value={form.beskrivning}
              onChange={(e) => set("beskrivning", e.target.value)}
              rows={4}
              className="input-mod"
              placeholder="Detaljer, öppettider, säsong, tillgänglighet, ev. kontaktansvarig…"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Källa (namn)" hint="T.ex. Strömsunds kommun, Räddningstjänst Jämtland.">
              <input
                data-testid="plats-kalltext"
                value={form.kalltext}
                onChange={(e) => set("kalltext", e.target.value)}
                className="input-mod"
              />
            </Field>
            <Field label="Länk till källa" error={errors.kalla_lank}>
              <input
                data-testid="plats-kalla-lank"
                value={form.kalla_lank}
                onChange={(e) => set("kalla_lank", e.target.value)}
                placeholder="https://…"
                className="input-mod"
              />
            </Field>
          </div>

          {/* Build 91 + Build 93b — fri taggning med autocomplete-pillar */}
          <Field label="Taggar / Mehrfachfunktion" hint="En plats kan höra till många teman samtidigt. Klicka förslagen eller skriv egna (komma-separerade).">
            <TagInput
              value={form.taggar}
              onChange={(v) => set("taggar", v)}
              testid="plats-taggar"
            />
          </Field>

          {/* Build 89 — valfri medie-länk (1 medium per post) */}
          <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-3">
            <Field label="Medietyp (valfritt)">
              <select
                data-testid="plats-media-typ"
                value={form.media_typ}
                onChange={(e) => set("media_typ", e.target.value)}
                className="input-mod"
              >
                <option value="">— (ingen) —</option>
                <option value="Video">Video</option>
                <option value="Artikel">Artikel</option>
                <option value="Audio">Audio</option>
                <option value="Dokument">Dokument</option>
              </select>
            </Field>
            <Field label="Medie-länk (URL)">
              <input
                data-testid="plats-media-url"
                value={form.media_url}
                onChange={(e) => set("media_url", e.target.value)}
                placeholder="https://… (valfritt)"
                className="input-mod"
              />
            </Field>
          </div>
          <Field label="Medie-beskrivning (kort, valfritt)">
            <textarea
              data-testid="plats-media-beskrivning"
              value={form.media_beskrivning}
              maxLength={300}
              onChange={(e) => set("media_beskrivning", e.target.value)}
              rows={2}
              placeholder="T.ex. Lokal-TV reportage om hjärtstartare."
              className="input-mod"
            />
          </Field>

          <Field label="Status">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="plats-publish"
                checked={form.publish}
                onChange={(e) => set("publish", e.target.checked)}
                className="w-5 h-5 accent-pine"
              />
              <span className="text-sm text-ink">
                Publicera direkt {form.publish ? <em className="text-pine">(visas på /karta)</em> : <em className="text-bark/60">(sparas som utkast)</em>}
              </span>
            </label>
          </Field>

          {/* Etappe 3 — Ändringsuppgifter (Pflicht) */}
          <div className="border-t border-warm-border/40 pt-4 mt-2">
            <h3 className="text-sm font-semibold text-ink mb-2">Ändringsuppgifter</h3>
            <Field label={<span>Ändrad av <span className="text-falu">*</span></span>} hint="Ange ditt namn — krävs av audit-trail." error={errors.andrad_av}>
              <input
                data-testid="plats-andrad-av"
                value={form.andrad_av}
                onChange={(e) => set("andrad_av", e.target.value)}
                placeholder="Ditt namn"
                className="input-mod"
              />
            </Field>
            <Field label="Ändringsnotering (valfri)" hint="Kort kommentar om vad som tillkommit/ändrats.">
              <input
                data-testid="plats-andringsnotering"
                value={form.andringsnotering}
                onChange={(e) => set("andringsnotering", e.target.value)}
                placeholder="T.ex. Nyligt foto tillagt, koordinat justerad"
                className="input-mod"
              />
            </Field>
          </div>
        </div>

        {/* Höger: karta + koordinatinmatning */}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-ink mb-1">
              Plats på karta <span className="text-falu">*</span>
            </label>
            <p className="text-xs text-bark/60 mb-2">
              Klicka på kartan eller dra markören till rätt plats. Du kan också klistra in koordinater nedan.
            </p>
            <div
              ref={mapRef}
              data-testid="plats-map"
              className="w-full h-[420px] bg-bone border border-warm-border rounded-sm"
            />
            <p className="text-xs font-mono text-bark/80 mt-2">
              <MapPin size={11} className="inline mr-1" />
              {pos.lat.toFixed(6)}, {pos.lng.toFixed(6)}
            </p>
          </div>

          <Field label="Klistra in koordinater eller Google Maps-länk" hint="Format: 63.81667, 16.4 eller https://maps.google.com/...">
            <div className="flex gap-2">
              <input
                data-testid="plats-coord-input"
                value={coordInput}
                onChange={(e) => setCoordInput(e.target.value)}
                placeholder="63.81667, 16.4"
                className="input-mod flex-1"
              />
              <button
                data-testid="plats-coord-apply"
                type="button"
                onClick={handleCoordPaste}
                className="bg-bark/80 hover:bg-bark text-parchment text-sm font-medium px-3 py-2 rounded-sm"
              >
                Använd
              </button>
            </div>
            {errors.coord && <p className="text-falu text-xs mt-1">{errors.coord}</p>}
          </Field>
        </div>
      </div>

      {/* Submit-rad */}
      <div className="mt-8 pt-6 border-t border-warm-border flex flex-wrap items-center gap-3">
        <button
          data-testid="plats-save-btn"
          onClick={() => submit()}
          disabled={busy}
          className="inline-flex items-center gap-1.5 bg-pine hover:bg-pine-hover disabled:opacity-50 text-white font-medium px-6 py-3 rounded-md text-base transition-colors"
        >
          <Save size={16} /> {busy ? "Sparar…" : form.publish ? "Skapa och publicera" : "Spara som utkast"}
        </button>
        <button
          data-testid="plats-save-draft-btn"
          onClick={() => submit(false)}
          disabled={busy}
          className="text-bark hover:text-ink text-sm font-medium px-4 py-3"
        >
          Spara som utkast utan att publicera
        </button>
        <button
          onClick={() => navigate("/mod")}
          className="text-muted-foreground hover:text-ink px-3 py-3 text-sm inline-flex items-center gap-1"
        >
          <ArrowLeft size={14} /> Avbryt
        </button>
        {errors.submit && <p className="text-falu text-sm w-full">{errors.submit}</p>}
      </div>
    </div>
  );
}

function Field({ label, error, required, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-ink mb-1">
        {label}{required && <span className="text-falu ml-1">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-bark/55 mt-1">{hint}</p>}
      {error && <p className="text-falu text-sm mt-1">{error}</p>}
    </div>
  );
}
